// pages/OrdersPage.tsx
import React, { useMemo } from "react";
import {
  Box,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useBreakpointValue,
  Button,
  VStack,
  HStack,
  Badge,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { Lock, LogIn } from "lucide-react";
import { useGetOrdersQuery } from "../../orderApi/orderApi";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";

const getOrderUserId = (order: any) =>
  String(
    order?.userId ||
      order?.uid ||
      order?.userUid ||
      order?.firebaseUid ||
      order?.customerId ||
      order?.customerUid ||
      order?.buyerId ||
      order?.buyerUid ||
      order?.user?.uid ||
      order?.customer?.uid ||
      order?.buyer?.uid ||
      "",
  );

const getOrdersArray = (orders: any) => {
  if (Array.isArray(orders)) return orders;
  if (Array.isArray(orders?.orders)) return orders.orders;
  if (Array.isArray(orders?.data)) return orders.data;
  return [];
};

const OrdersPage = () => {
  const navigate = useNavigate();
  const authContext = useAuth() as any;
  const user = authContext?.user;
  const authLoading = authContext?.loading || authContext?.isLoading || false;
  const { theme } = useCustomTheme();

  const {
    data: orders,
    isLoading,
    isError,
  } = useGetOrdersQuery(undefined, {
    skip: !user || authLoading,
  });

  // Responsive table sizing
  const tableSize = useBreakpointValue({ base: "xs", md: "sm" });
  const minTableWidth = useBreakpointValue({ base: "500px", md: "auto" });

  const pageBg = theme.background;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const borderColor = theme.border;
  const borderStrong = theme.borderStrong;
  const subtleText = theme.textMuted;
  const headingText = theme.text;
  const cardShadow = theme.shadowSm;
  const panelShadow = theme.shadowMd;
  const isDark = theme.name === "dark";
  const cardsBorderRadii = "8px";

  const myOrders = useMemo(() => {
    const list = getOrdersArray(orders);

    if (!user?.uid) return [];

    return list.filter((order: any) => getOrderUserId(order) === user.uid);
  }, [orders, user?.uid]);

  if (authLoading) return <GlobalSpinner />;

  if (!user) {
    return (
      <Box
        bg={pageBg}
        minH="100%"
        p={{ base: 4, md: 6 }}
        color={theme.text}
        borderRadius={cardsBorderRadii}
      >
        <Box mx="auto">
          <Box
            bg={cardBg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius={cardsBorderRadii}
            p={{ base: 4, md: 6 }}
            boxShadow={panelShadow}
            mb={6}
            position="relative"
            overflow="hidden"
          >
            <Flex
              direction={{ base: "column", md: "row" }}
              justify="space-between"
              align={{ base: "flex-start", md: "center" }}
              gap={4}
            >
              <Box>
                <HStack spacing={2} mb={2} flexWrap="wrap">
                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(39,183,255,0.14)" : "rgba(39,183,255,0.08)"
                    }
                    color={isDark ? "blue.200" : "blue.700"}
                    fontWeight="700"
                  >
                    Orders
                  </Badge>

                  <Badge
                    px={3}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    bg={
                      isDark ? "rgba(239,68,68,0.16)" : "rgba(239,68,68,0.10)"
                    }
                    color={isDark ? "red.200" : "red.700"}
                    fontWeight="700"
                  >
                    Login required
                  </Badge>
                </HStack>

                <Heading size="lg" color={headingText}>
                  Sign in to view your orders
                </Heading>

                <Text mt={2} color={subtleText} fontWeight="500" maxW="760px">
                  Your orders are private. Sign in first to view only the orders
                  linked to your account.
                </Text>
              </Box>

              <HStack
                spacing={2}
                px={3}
                py={2}
                borderRadius={cardsBorderRadii}
                border={`1px solid ${borderColor}`}
                bg={isDark ? "rgba(255,255,255,0.03)" : softBg}
                boxShadow={cardShadow}
              >
                <Box w="8px" h="8px" borderRadius={cardsBorderRadii} bg={theme.primary} />
                <Text color={headingText} fontWeight="700" fontSize="sm">
                  Secure orders
                </Text>
              </HStack>
            </Flex>
          </Box>

          <Box
            borderRadius={cardsBorderRadii}
            bg={cardBg}
            boxShadow={cardShadow}
            border={`1px solid ${borderColor}`}
            p={{ base: 5, md: 6 }}
          >
            <VStack spacing={4} align="start">
              <Flex
                w="56px"
                h="56px"
                borderRadius={cardsBorderRadii}
                align="center"
                justify="center"
                bg={isDark ? "rgba(239,68,68,0.12)" : "rgba(239,68,68,0.08)"}
                border={`1px solid ${
                  isDark ? "rgba(239,68,68,0.22)" : "rgba(239,68,68,0.16)"
                }`}
              >
                <Icon
                  as={Lock}
                  boxSize={6}
                  color={isDark ? "red.200" : "red.600"}
                />
              </Flex>

              <Box>
                <Text color={headingText} fontWeight="800" fontSize="lg">
                  You need to be logged in
                </Text>
                <Text color={subtleText} mt={1}>
                  Sign in to view your order history, payments, delivery status,
                  invoices, and booking records.
                </Text>
              </Box>

              <HStack spacing={3} pt={1} flexWrap="wrap">
                <Button
                  leftIcon={<LogIn size={16} />}
                  color={theme.textOnPrimary}
                  bgImage={theme.gradients?.primaryButton || theme.primary}
                  _hover={{
                    bgImage:
                      theme.gradients?.primaryButtonHover || theme.primaryHover,
                    transform: "translateY(-1px)",
                  }}
                  _active={{ transform: "translateY(0)" }}
                  borderRadius={cardsBorderRadii}
                  boxShadow={theme.shadowSm}
                  transition="all 0.2s ease"
                  onClick={() => navigate("/login")}
                >
                  Sign In
                </Button>

                <Button
                  variant="outline"
                  borderColor={borderStrong}
                  color={headingText}
                  bg={cardBg}
                  _hover={{ bg: theme.hover }}
                  borderRadius={cardsBorderRadii}
                  onClick={() => navigate("/dashboard")}
                >
                  Go back
                </Button>
              </HStack>
            </VStack>
          </Box>
        </Box>
      </Box>
    );
  }

  if (isLoading) return <GlobalSpinner />;
  if (isError) return <Text color="red.500">Failed to load orders.</Text>;

  return (
    <Box bg="white" borderRadius={cardsBorderRadii}>
      <Heading p={6} fontSize={{ base: "lg", md: "2xl" }}>
        My Orders
      </Heading>

      <Box bg="white" boxShadow="md" overflowX="auto">
        <Table
          variant="simple"
          size={tableSize}
          minW={minTableWidth}
          whiteSpace="nowrap"
        >
          <Thead>
            <Tr bg="#fff" height="50px" borderTop={"1px solid #EDF2F7"}>
              <Th px={{ base: 2, md: 4 }}>Order ID</Th>
              <Th px={{ base: 2, md: 4 }}>Total</Th>
              <Th px={{ base: 2, md: 4 }}>Address</Th>
              <Th px={{ base: 2, md: 4 }}>Date</Th>
            </Tr>
          </Thead>
          <Tbody>
            {myOrders.length === 0 ? (
              <Tr>
                <Td colSpan={4} px={{ base: 2, md: 4 }} py={8}>
                  <Text textAlign="center" color="gray.500">
                    You do not have any orders yet.
                  </Text>
                </Td>
              </Tr>
            ) : (
              myOrders.map((order: any, idx: number) => (
                <Tr
                  key={order._id || order.id}
                  bg={idx % 2 === 0 ? "gray.50" : "white"}
                  height="50px"
                >
                  <Td
                    px={{ base: 2, md: 4 }}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {order._id || order.id}
                  </Td>
                  <Td
                    px={{ base: 2, md: 4 }}
                    fontSize={{ base: "xs", md: "sm" }}
                    fontWeight="bold"
                  >
                    ${Number(order.total || 0).toFixed(2)}
                  </Td>
                  <Td
                    px={{ base: 2, md: 4 }}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {[order.address, order.city].filter(Boolean).join(", ") ||
                      "—"}
                  </Td>
                  <Td
                    px={{ base: 2, md: 4 }}
                    fontSize={{ base: "xs", md: "sm" }}
                  >
                    {order.createdAt
                      ? new Date(order.createdAt).toLocaleDateString()
                      : "—"}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>
    </Box>
  );
};

export default OrdersPage;
